import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState } from "@/components/common";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { fmtUSDStrict } from "@/lib/format";
import {
  Coins, Repeat, TrendingUp, Split, PlayCircle, RefreshCw, Banknote, Landmark, Download,
} from "lucide-react";

const day = (d) => (d && d !== "—" ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function Dividends() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [statement, setStatement] = useState(null);
  const [year, setYear] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatement = useCallback(async (y) => {
    try {
      const { data: s } = await api.get("/corporate-actions/statement",
        { params: y ? { year: y } : {} });
      setStatement(s);
    } catch (e) {
      console.error("Failed to load dividend statement:", e);
    }
  }, []);

  const pickYear = (y) => { setYear(y); loadStatement(y); };

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/corporate-actions/summary"), api.get("/corporate-actions/history"),
      ]);
      setData(s.data);
      setHistory(h.data.events);
      setYear(null);
      loadStatement(null);
    } catch (e) {
      console.error("Failed to load corporate actions:", e);
      toast.error("Couldn't load your dividends", { description: "Please refresh to try again." });
    }
  }, [loadStatement]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/corporate-actions/run");
      toast.success(res.applied ? `${res.applied} action${res.applied > 1 ? "s" : ""} applied`
                                : "Nothing outstanding",
        { description: res.applied ? `${fmtUSDStrict(res.cash_paid_usd)} paid into your wallet.`
                                   : "Every dividend and split on your holdings is up to date." });
      load();
    } catch (e) {
      toast.error("Couldn't apply those actions", { description: e?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const toggleReinvest = async (on) => {
    setData((d) => ({ ...d, reinvest: on }));
    try {
      await api.post("/corporate-actions/reinvest", { reinvest: on });
      toast.success(on ? "Dividends will be reinvested" : "Dividends will be paid as cash",
        { description: on ? "Each payment buys more of the share that paid it."
                          : "Each payment lands in your USD wallet." });
    } catch (e) {
      setData((d) => ({ ...d, reinvest: !on }));
      toast.error("Couldn't save that", { description: e?.response?.data?.detail });
    }
  };

  const simulate = async (symbol) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/corporate-actions/simulate", { symbol });
      toast.success(`${symbol} dividend paid`, {
        description: res.reinvested
          ? `${res.shares_bought} more shares bought with ${fmtUSDStrict(res.amount_usd)}.`
          : `${fmtUSDStrict(res.amount_usd)} credited to your USD wallet.`,
      });
      load();
    } catch (e) {
      toast.error("Couldn't replay that dividend", { description: e?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  if (!data) return <AppShell><div className="p-8 text-sm text-muted-foreground">Loading...</div></AppShell>;

  return (
    <AppShell>
      <div data-testid="dividends-page">
        <PageHeader title={t("dv_title")} subtitle={t("dv_sub")} testid="dividends-header"
          action={
            <Button onClick={run} disabled={busy} data-testid="dividends-run-button"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <RefreshCw className={`h-4 w-4 me-1.5 ${busy ? "animate-spin" : ""}`} />
              {data.pending.length ? `Apply ${data.pending.length} outstanding` : "Check for new actions"}
            </Button>
          } />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Kpi label="Dividends received (net)" value={fmtUSDStrict(data.received.total_usd)}
            icon={Coins} tint={0} accent testid="dividends-kpi-total" />
          <Kpi label="Last 12 months" value={fmtUSDStrict(data.received.last_12m_usd)}
            icon={Banknote} tint={1} testid="dividends-kpi-12m" />
          <Kpi label="Tax withheld at source" value={fmtUSDStrict(data.received.tax_withheld_usd || 0)}
            icon={Landmark} tint={2} testid="dividends-kpi-tax" />
          <Kpi label="Forward estimate / yr (net)" value={fmtUSDStrict(data.estimated_annual_usd)}
            icon={TrendingUp} tint={3} testid="dividends-kpi-forecast" />
        </div>

        <div className="grid gap-4 sm:gap-5 lg:grid-cols-3 mt-4 sm:mt-5 min-w-0">
          <Card className="p-5 shadow-premium lg:col-span-2 min-w-0 overflow-hidden" data-testid="dividends-holdings-card">
            <div className="flex items-center gap-2 text-sm font-semibold mb-3">
              <Coins className="h-4 w-4" />What your holdings pay
            </div>
            {data.holdings.length === 0 ? (
              <EmptyState title="No income-paying holdings yet"
                hint="Buy a share or ETF and its dividends will land here automatically."
                testid="dividends-empty" />
            ) : (
              <div className="overflow-x-auto min-w-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground text-[11px] uppercase tracking-[0.08em]">
                      <th className="text-start font-semibold pb-2">Holding</th>
                      <th className="text-end font-semibold pb-2">Shares</th>
                      <th className="text-end font-semibold pb-2 hidden sm:table-cell">Yield</th>
                      <th className="text-end font-semibold pb-2 hidden md:table-cell">Tax at source</th>
                      <th className="text-end font-semibold pb-2">Est. net / yr</th>
                      <th className="text-end font-semibold pb-2 hidden sm:table-cell">Ex-date</th>
                      {data.can_simulate && <th className="text-end font-semibold pb-2"> </th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.holdings.map((h) => (
                      <tr key={h.symbol} className="border-t border-border/60"
                        data-testid={`dividends-row-${h.symbol}`}>
                        <td className="py-2.5">
                          <div className="font-semibold">{h.symbol}</div>
                          <div className="text-[11px] text-muted-foreground truncate max-w-[120px] sm:max-w-[180px]">{h.name}</div>
                        </td>
                        <td className="py-2.5 text-end numeric">{Number(h.qty).toLocaleString()}</td>
                        <td className="py-2.5 text-end numeric hidden sm:table-cell">{h.yield_pct ? `${h.yield_pct}%` : "—"}</td>
                        <td className="py-2.5 text-end hidden md:table-cell" data-testid={`dividends-tax-${h.symbol}`}>
                          <div className="numeric text-xs">{h.withholding_pct ? `${h.withholding_pct}%` : "0%"}</div>
                          <div className="text-[10px] text-muted-foreground truncate max-w-[110px]">{h.withholding_country}</div>
                        </td>
                        <td className="py-2.5 text-end numeric font-semibold">{fmtUSDStrict(h.estimated_annual_net_usd ?? h.estimated_annual_usd)}</td>
                        <td className="py-2.5 text-end text-xs text-muted-foreground hidden sm:table-cell">{day(h.next_ex_date)}</td>
                        {data.can_simulate && (
                          <td className="py-2.5 text-end">
                            <Button variant="ghost" size="sm" className="h-7 text-[11px]"
                              disabled={busy} onClick={() => simulate(h.symbol)}
                              data-testid={`dividends-simulate-${h.symbol}`}>
                              <PlayCircle className="h-3.5 w-3.5 me-1" />Replay last
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              Dividend and split records come from company filings on Yahoo Finance. We only pay you
              for actions dated after you opened the position, and each one is applied once. Estimates
              are shown net of the paying country's withholding tax.
            </p>
          </Card>

          <div className="space-y-4 sm:space-y-5">
            <Card className="p-5 shadow-premium" data-testid="dividends-reinvest-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span className="icon-chip bg-emerald-500/12 text-emerald-700"><Repeat className="h-4 w-4" /></span>
                  <div>
                    <div className="text-sm font-semibold">{t("dv_reinvest")}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {data.reinvest ? "On — payments buy more shares" : "Off — payments stay as cash"}
                    </div>
                  </div>
                </div>
                <Switch checked={!!data.reinvest} onCheckedChange={toggleReinvest}
                  data-testid="dividends-reinvest-switch" />
              </div>
              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">{t("dv_reinvest_hint")}</p>
            </Card>

            <Card className="p-5 shadow-premium" data-testid="dividends-statement-card">
              <div className="flex items-center gap-2 text-sm font-semibold mb-3">
                <Landmark className="h-4 w-4" />Tax statement
              </div>
              {statement && (
                <>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <Button variant={year === null ? "default" : "outline"} size="sm"
                      className="h-7 text-[11px]" onClick={() => pickYear(null)}
                      data-testid="dividends-year-all">All time</Button>
                    {statement.years.map((y) => (
                      <Button key={y} variant={year === y ? "default" : "outline"} size="sm"
                        className="h-7 text-[11px]" onClick={() => pickYear(y)}
                        data-testid={`dividends-year-${y}`}>{y}</Button>
                    ))}
                  </div>
                  <div className="space-y-1.5 text-xs" data-testid="dividends-statement-totals">
                    <div className="flex justify-between"><span className="text-muted-foreground">Gross paid</span>
                      <span className="numeric font-semibold">{fmtUSDStrict(statement.totals.gross_usd)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Tax withheld</span>
                      <span className="numeric font-semibold text-rose-600">
                        -{fmtUSDStrict(statement.totals.tax_usd)}
                        <span className="text-muted-foreground font-normal ms-1">({statement.totals.effective_tax_pct}%)</span>
                      </span></div>
                    <div className="flex justify-between border-t border-border/60 pt-1.5">
                      <span className="text-muted-foreground">Net received</span>
                      <span className="numeric font-semibold text-emerald-600">{fmtUSDStrict(statement.totals.net_usd)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Payments</span>
                      <span className="numeric">{statement.totals.payments}</span></div>
                  </div>
                  {statement.by_country.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {statement.by_country.slice(0, 5).map((cn) => (
                        <div key={cn.country} className="flex justify-between text-[11px]">
                          <span className="text-muted-foreground truncate">{cn.country}</span>
                          <span className="numeric">-{fmtUSDStrict(cn.tax_usd)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="w-full mt-3 h-8 text-xs"
                    disabled={!statement.totals.payments}
                    onClick={() => window.open(
                      `${process.env.REACT_APP_BACKEND_URL}/api/corporate-actions/statement.pdf${year ? `?year=${year}` : ""}`,
                      "_blank")}
                    data-testid="dividends-statement-pdf">
                    <Download className="h-3.5 w-3.5 me-1.5" />Download {statement.label} statement
                  </Button>
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    Dividend tax is deducted by the paying country before the money reaches us, so
                    your wallet is credited net. A lower treaty rate may apply once your residency
                    form is on file.
                  </p>
                </>
              )}
            </Card>

            {data.pending.length > 0 && (              <Card className="p-5 shadow-premium" data-testid="dividends-pending-card">
                <div className="text-sm font-semibold mb-2">Waiting to be applied</div>
                <div className="space-y-2">
                  {data.pending.slice(0, 6).map((p) => (
                    <div key={p.action_id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="font-semibold">{p.symbol}</span>
                      <span className="text-muted-foreground">
                        {p.kind === "split" ? `${p.ratio}-for-1 split` : `${p.per_share} / share`}
                      </span>
                      <span className="text-muted-foreground">{day(p.date)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-5 shadow-premium" data-testid="dividends-history-card">
              <div className="text-sm font-semibold mb-3">Applied to your account</div>
              {history.length === 0 ? (
                <EmptyState compact testid="dividends-history-empty" title="Nothing yet"
                  hint="Dividends and splits appear here the moment we apply them." />
              ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto">
                  {history.map((e) => (
                    <div key={e.id} className="flex items-start justify-between gap-3"
                      data-testid={`dividends-event-${e.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          {e.kind === "split" ? <Split className="h-3.5 w-3.5" /> : <Coins className="h-3.5 w-3.5" />}
                          {e.symbol}
                          {e.simulated && <Badge variant="secondary" className="text-[9px]">replay</Badge>}
                          {e.reinvested && <Badge className="text-[9px]">reinvested</Badge>}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {e.kind === "split"
                            ? `${e.qty_before} → ${e.qty_after} shares · ${day(e.date)}`
                            : `${e.per_share} / share on ${Number(e.qty).toLocaleString()} · ${day(e.date)}`}
                        </div>
                        {e.kind === "dividend" && e.tax_usd > 0 && (
                          <div className="text-[11px] text-muted-foreground"
                            data-testid={`dividends-event-tax-${e.id}`}>
                            {fmtUSDStrict(e.gross_usd)} gross · {e.tax_country} withheld{" "}
                            {fmtUSDStrict(e.tax_usd)} ({e.tax_rate_pct}%)
                          </div>
                        )}
                      </div>
                      <span className="numeric text-sm font-semibold shrink-0 text-emerald-600">
                        {e.kind === "split" ? "—" : `+${fmtUSDStrict(e.amount_usd)}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
